import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { apiService } from '@/services/api';

/**
 * Where a shared referral link lands (/refer/:branchId/:code). We remember the referral, and:
 *  - if the visitor is already registered, claim it now and show their welcome reward;
 *  - if not, invite them to sign up — the pending referral is stored and claimed automatically
 *    right after they register (see PendingReferralClaim in App).
 */
const PENDING_KEY = 'pendingReferral';

const ReferralLanding: React.FC = () => {
  const { branchId, code } = useParams<{ branchId: string; code: string }>();
  const navigate = useNavigate();
  const [state, setState] = useState<'loading' | 'signup' | 'claimed' | 'failed'>('loading');
  const [message, setMessage] = useState('');
  const [branchName, setBranchName] = useState('');

  useEffect(() => {
    if (!branchId || !code) { setState('failed'); setMessage('This referral link looks incomplete.'); return; }

    // Remember it either way, so signup → claim works.
    try { localStorage.setItem(PENDING_KEY, JSON.stringify({ branchId, code })); } catch { /* ignore */ }

    (async () => {
      try { const b = await apiService.getBranchDetails(branchId); setBranchName(((b as any)?.data?.name) || ''); } catch { /* ignore */ }

      if (!apiService.isAuthenticated()) { setState('signup'); return; }

      try {
        const res = await apiService.recordReferral(code, branchId);
        const data: any = (res as any)?.data || {};
        if (data.ok) {
          try { localStorage.removeItem(PENDING_KEY); } catch { /* ignore */ }
          setState('claimed');
        } else {
          setState('failed');
          setMessage(reasonText(data.reason));
        }
      } catch (e) {
        setState('failed');
        setMessage(e instanceof Error ? e.message : 'Could not claim this referral.');
      }
    })();
  }, [branchId, code]);

  return (
    <div style={s.wrap}>
      <div style={s.card}>
        <div style={s.emoji}>🎁</div>
        {state === 'loading' && <p style={s.sub}>One moment…</p>}

        {state === 'signup' && (
          <>
            <h1 style={s.h1}>You've been invited{branchName ? ` to ${branchName}` : ''}!</h1>
            <p style={s.sub}>Sign up to claim your welcome discount. You and the person who invited you both get a reward.</p>
            <button style={s.primary} onClick={() => navigate('/signup')}>Sign up & claim</button>
            <button style={s.ghost} onClick={() => navigate('/login')}>I already have an account</button>
          </>
        )}

        {state === 'claimed' && (
          <>
            <h1 style={s.h1}>Welcome reward unlocked 🎉</h1>
            <p style={s.sub}>Your discount is saved to your rewards{branchName ? ` at ${branchName}` : ''}. Show it at the table — it comes off your bill.</p>
            <button style={s.primary} onClick={() => navigate('/')}>Explore</button>
          </>
        )}

        {state === 'failed' && (
          <>
            <h1 style={s.h1}>Couldn't claim this one</h1>
            <p style={s.sub}>{message || 'This referral could not be applied.'}</p>
            <button style={s.ghost} onClick={() => navigate('/')}>Continue</button>
          </>
        )}
      </div>
    </div>
  );
};

function reasonText(reason?: string): string {
  switch (reason) {
    case 'no_active_campaign': return 'This venue isn’t running a referral offer right now.';
    case 'invalid_code': return 'That referral code isn’t valid.';
    case 'self_referral': return 'You can’t refer yourself.';
    case 'already_referred': return 'You’ve already been referred to this venue.';
    case 'existing_customer': return 'Referrals are for guests who are new to this venue.';
    default: return 'This referral could not be applied.';
  }
}

const s: Record<string, React.CSSProperties> = {
  wrap: { minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16, background: '#f9fafb' },
  card: { background: '#fff', border: '1px solid #e5e7eb', borderRadius: 18, padding: 28, maxWidth: 400, width: '100%', textAlign: 'center', boxShadow: '0 4px 20px rgba(0,0,0,0.06)' },
  emoji: { fontSize: 44, marginBottom: 8 },
  h1: { fontSize: 22, fontWeight: 800, color: '#111827', margin: '0 0 8px' },
  sub: { color: '#6b7280', fontSize: 14.5, margin: '0 0 18px', lineHeight: 1.5 },
  primary: { width: '100%', background: '#16a34a', color: '#fff', border: 'none', borderRadius: 12, padding: '13px 16px', fontWeight: 800, cursor: 'pointer', marginBottom: 10, fontSize: 15 },
  ghost: { width: '100%', background: '#fff', color: '#374151', border: '1px solid #d1d5db', borderRadius: 12, padding: '12px 16px', fontWeight: 700, cursor: 'pointer' },
};

export default ReferralLanding;
